import { Map as ImmutableMap } from 'immutable';
import * as React from 'react';
import { useRef } from 'react';

import type { accountDiscoveryTypes } from 'app-components/flux/account-discovery';
import { accountDiscoveryUtils } from 'app-components/flux/account-discovery';
import type { accountsTypes } from 'app-components/flux/accounts';
import { accountsUtils } from 'app-components/flux/accounts';
import { businessSelectors, type businessTypes } from 'app-components/flux/business';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

import type { FormikProps } from 'formik';
import { Field, getIn } from 'formik';

import AccountTypeSelector from 'components/Formik/AccountTypeSelector';
import FormikCheckbox from 'components/Formik/Checkbox';
import FormikTextField from 'components/Formik/TextField';
import AmountField from 'components/QuickenControls/AmountField';
import UsageAndEntitySelector from 'components/UsageAndEntitySelector';

import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import LinkActionSelector from './LinkActionSelector';

type Props = {
  // @ts-expect-error FIXME TS2694
  account: accountDiscoveryTypes.AccountData;
  accountsById: ImmutableMap<string, accountsTypes.Account>;
  businessEnabled: boolean;
  businessesById?: ImmutableMap<string, businessTypes.Business>;
  index: number;

  enableLinkTo: boolean;
  stagedLinkToIds: string[];
  stagedNames?: string[];

  formik: FormikProps<any>;
};

const useStyles = makeStyles()((theme) => ({
  root: {
    overflow: 'hidden',
    padding: '16px 0px',
    border: `1px ${theme.palette.border.light} solid`,
    borderRadius: 10,
    marginBottom: 12,
    boxShadow: theme.shadows[1],
  },
  checkbox: {
    paddingTop: 7,
    alignSelf: 'start',
  },
}));

const availableBusinessesById = createSelector(
  [businessSelectors.getAvailableBusinessesForStripeAccount],
  (availableBusinesses) => {
    return ImmutableMap(availableBusinesses.map((item) => [item.id, item]));
  },
);

const AccountRow = (props: Props) => {
  const { account, accountsById, businessEnabled, enableLinkTo, formik, index, stagedLinkToIds, stagedNames } = props;

  const nameRef = useRef<HTMLInputElement>(undefined);
  const { classes } = useStyles();

  const isStripeAccount =
    account.discoveredAccount.channel === 'STRIPE_TEST' || account.discoveredAccount.channel === 'STRIPE';

  const businessesById = useSelector(isStripeAccount ? availableBusinessesById : businessSelectors.getResourcesById);

  const enabledFieldName = `${account.formFieldName}.enabled`;
  const nameFieldName = `${account.formFieldName}.name`;
  const typeFieldName = `${account.formFieldName}.type`;
  const usageIdFieldName = `${account.formFieldName}.usageId`;
  const linkToIdFieldName = `${account.formFieldName}.linkToId`;

  const enabled = getIn(formik.values, enabledFieldName);
  const name = getIn(formik.values, nameFieldName);
  const linkToId = getIn(formik.values, linkToIdFieldName);

  const accounts = accountsById.valueSeq().toList();

  const getMaskedNumber = () => {
    const { maskedNumber } = account.discoveredAccount;
    if (!maskedNumber) {
      return undefined;
    }
    const charactersNum = maskedNumber.length >= 4 ? 4 : maskedNumber.length;
    const lastCharacters = maskedNumber && maskedNumber.slice(-charactersNum);
    const last4Characters =
      lastCharacters && !Number.isNaN(Number(lastCharacters)) ? 'X'.repeat(4 - charactersNum) + lastCharacters : 'XXXX';
    return `•••${last4Characters}`;
  };

  // @ts-expect-error FIXME TS7006
  const handleLinkToChange = (event) => {
    const newLinkTo = event.target.value;

    const values = accountDiscoveryUtils.getNewAccountFields(account, newLinkTo, accountsById);
    formik.setFieldValue(nameFieldName, values.name);
    formik.setFieldValue(typeFieldName, values.type);
    formik.setFieldValue(usageIdFieldName, values.usageId);
  };

  // @ts-expect-error FIXME TS7006
  const validateName = (stagedName) => {
    if (!enabled) return null;
    let ret: string | null = null;

    const lowerCaseName = stagedName?.toLowerCase();
    if (!lowerCaseName) {
      return 'Account name required';
    }

    if (accountsUtils.isInvalidName(stagedName)) {
      ret = 'Invalid account name.';
    }
    // Check that name is unique across all other accounts. Note: There is a corner case that will give a false
    // error (changing two account names while linking to each other).
    //
    if (accounts) {
      accounts.forEach((otherAccount) => {
        if (linkToId !== otherAccount.id && otherAccount.name?.toLowerCase() === lowerCaseName) {
          ret = 'Account name must be unique.';
        }
      });
    }

    // Check that two staged-names are not the same.
    let count = 0;
    stagedNames?.forEach((otherName) => {
      if (otherName === lowerCaseName) {
        count += 1;
      }
    });
    if (count > 1) {
      ret = 'Account name must be unique.';
    }

    return ret;
  };

  // @ts-expect-error FIXME TS7006
  const validateType = (type) => {
    if (!enabled) return null;
    let ret: string | null = null;

    if (type.qcsType === 'UNKNOWN') {
      ret = 'Please pick an account type';
    }
    return ret;
  };

  // Check that staged link-to IDs are unique (cannot link more than once to each account)
  // @ts-expect-error FIXME TS7006
  const validateLinkToId = (stagedLinkToId) => {
    if (!enabled || stagedLinkToId === '0') return null;

    let count = 0;
    stagedLinkToIds?.forEach((otherLinkToId) => {
      if (stagedLinkToId === otherLinkToId) {
        count += 1;
      }
    });
    return count > 1 ? 'Cannot link to same account' : null;
  };

  const showNameTip = nameRef.current && nameRef.current.offsetWidth < nameRef.current.scrollWidth;
  const fiNameToShow = name !== account.discoveredAccount.name ? account.discoveredAccount.name : null;

  return (
    <Stack direction='row' className={classes.root}>
      <Field component={FormikCheckbox} name={enabledFieldName} disableRipple className={classes.checkbox} />
      <Stack spacing={1} flex='1' maxWidth='649px'>
        <Stack direction='row' flexGrow='1' spacing={1}>
          <Tooltip title={showNameTip ? name : ''}>
            <Box
            // don't remove this div - it is important for catching the tooltip ref, as field
            // is a 3rd party functional component and therefore cannot hold a ref
            /* eslint-disable-next-line react/jsx-closing-bracket-location */
            >
              <Field
                component={FormikTextField}
                disabled={!enabled}
                id={`account-${index}-name`}
                name={nameFieldName}
                variant='outlined'
                validate={validateName}
                InputProps={{
                  id: `account#-${index}-name`,
                  ref: nameRef,
                  sx: { typography: 'body2' },
                  variant: 'ellipsis',
                }}
                size='small'
                sx={{ minWidth: businessEnabled ? '265px' : '373px' }}
              />
            </Box>
          </Tooltip>
          <AccountTypeSelector
            currentAccountType={account.type}
            name={typeFieldName}
            showManualTypes={false}
            size='small'
            sx={{ flex: 1 }}
            validate={validateType}
            TextFieldProps={{
              disabled: !enabled || formik.values[account.formFieldName].linkToId !== '0',
              readOnly: isStripeAccount,
              id: `account-${index}-type`,
              label: null,
              variant: 'outlined',
              ...(!businessEnabled && { fullWidth: true }),
            }}
          />
          {businessEnabled && (
            <UsageAndEntitySelector
              businessesById={businessesById}
              disabled={!enabled}
              name={usageIdFieldName}
              hidePersonal={isStripeAccount}
              sx={{
                flex: 1,
                '& .MuiSelect-select': {
                  display: 'flex',
                },
              }}
            />
          )}
        </Stack>

        <Stack direction='row' flexGrow='1' spacing={1}>
          <Stack
            alignSelf='center'
            direction='column'
            sx={{
              ...(!enabled && { color: 'text.disabled' }),
              minWidth: businessEnabled ? '265px' : '373px',
            }}
          >
            <Typography variant='captionRegular'>
              {account.discoveredAccount.maskedNumber ? `Acct: ${getMaskedNumber()}` : null}
              {account.discoveredAccount.balance && (
                <>
                  &nbsp;&#8226;&nbsp;Balance:&nbsp;
                  <AmountField
                    id={`account-${index}-balance`}
                    value={account.discoveredAccount.balance}
                    currencySymbol={'USD'}
                    variant='captionRegular'
                  />
                </>
              )}
            </Typography>
            {fiNameToShow && <Typography variant='captionRegular'>{`Name: ${fiNameToShow}`}</Typography>}
          </Stack>
          {enableLinkTo && (
            <LinkActionSelector
              name={linkToIdFieldName}
              accountsById={accountsById}
              disabled={!enabled}
              linkableAccountIdsSection1={account.linkableAccountIdsSection1}
              linkableAccountIdsSection2={account.linkableAccountIdsSection2}
              linkableAccountIdsSection3={account.linkableAccountIdsSection3}
              onBlur={formik.handleBlur}
              onChange={handleLinkToChange}
              sx={{ flex: 1.5 }}
              variant='outlined'
              validate={validateLinkToId}
            />
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default AccountRow;
